import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, ShoppingCart, Package, BoxesIcon, Boxes, Truck, BarChart3,
  Store, Users as UsersIcon, ShieldCheck, History, CreditCard,
  Percent, QrCode, LogOut, Menu, X, Building2, Receipt, Sparkles, GitCompare, Banknote
} from "lucide-react";
import { cn } from "@/lib/utils";
import BrandMark from "@/components/BrandMark";

const tenantNav = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", perm: "dashboard.view", end: true },
  { to: "/app/pos", icon: ShoppingCart, label: "Kasir POS", perm: "pos.use", feature: "pos" },
  { to: "/app/sales", icon: Receipt, label: "Penjualan", perm: "sales.view", feature: "sales_history" },
  { to: "/app/products", icon: Package, label: "Produk & Barcode", perm: "products.view", feature: "product_management" },
  { to: "/app/stock", icon: Boxes, label: "Stok", perm: "stock.view", feature: "stock_management" },
  { to: "/app/purchases", icon: Truck, label: "Pembelian", perm: "purchases.view", feature: "purchases" },
  { to: "/app/reports", icon: BarChart3, label: "Laporan", perm: "reports.view", feature: "reports" },
  { to: "/app/reconciliation", icon: GitCompare, label: "Rekonsiliasi QRIS", perm: "reconciliation.view", feature: "reconciliation" },
  { to: "/app/outlets", icon: Store, label: "Outlet", perm: "outlets.manage" },
  { to: "/app/users", icon: UsersIcon, label: "Pengguna", perm: "users.manage", feature: "team_management" },
  { to: "/app/role-permissions", icon: ShieldCheck, label: "Role & Akses", perm: "users.manage", feature: "team_management" },
  { to: "/app/promos", icon: Percent, label: "Promo & Diskon", perm: "promos.manage", feature: "promos" },
  { to: "/app/qris", icon: QrCode, label: "QRIS Outlet", perm: "qris.manage", feature: "qris" },
  { to: "/app/audit", icon: History, label: "Audit Log", perm: "audit.view", feature: "audit_logs" },
  { to: "/app/subscription", icon: CreditCard, label: "Langganan", perm: "subscription.view" },
];

const platformNav = [
  { to: "/platform", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/platform/mitras", icon: Building2, label: "Mitra" },
  { to: "/platform/plans", icon: Sparkles, label: "Paket Harga" },
  { to: "/platform/qris", icon: QrCode, label: "Pembayaran Langganan" },
  { to: "/platform/payments", icon: Banknote, label: "Pembayaran" },
];

export default function Layout({ platform = false }) {
  const { user, tenant, logout, hasPerm, hasFeature, subscription, plan, entitlements } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const nav = platform
    ? platformNav
    : tenantNav.filter((item) => hasPerm(item.perm) && hasFeature(item.feature));

  return (
    <div className="min-h-screen bg-white text-zinc-900 flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 z-40 h-screen w-72 shrink-0 bg-white border-r border-border flex flex-col transition-transform",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div onClick={() => navigate("/")} className="cursor-pointer" data-testid="brand-link">
            <div className="overline text-zinc-500">POS Multi-Outlet</div>
            <BrandMark accentClassName="text-blue-600" />
          </div>
          <button className="lg:hidden p-2" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-border">
          <div className="overline text-zinc-500">
            {platform ? "Platform Owner" : (tenant?.name || "Mitra")}
          </div>
          <div className="text-sm font-semibold mt-1">{user?.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5 uppercase tracking-wider">{user?.role}</div>
          {!platform && subscription && (
            <div className="mt-2 space-y-2">
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-border bg-zinc-50">
                {plan?.name || entitlements?.plan_name || "Trial"} · {subscription.status}
              </div>
              {entitlements?.usage && (
                <div className="text-[10px] leading-relaxed text-zinc-500 tabular-nums">
                  {entitlements.usage.outlets}/{entitlements.max_outlets} outlet · {entitlements.usage.users}/{entitlements.max_users} pengguna
                </div>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors",
                  isActive
                    ? "border-[#002FA7] bg-zinc-50 text-zinc-900 font-semibold"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                )
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4 space-y-2">
          {user?.role === "platform_owner" && (
            <button
              onClick={() => navigate(platform ? "/app" : "/platform")}
              className="w-full text-xs uppercase tracking-widest font-semibold py-2 border border-border hover:bg-zinc-900 hover:text-white transition-colors"
              data-testid="switch-mode-btn"
            >
              {platform ? "Mode Mitra" : "Mode Platform"}
            </button>
          )}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-semibold py-2 border border-border hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white border-b border-border h-14 flex items-center px-4 lg:px-8 justify-between">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-5 w-5" />
          </button>
          <div className="overline text-zinc-500 hidden lg:block">{location.pathname}</div>
          <div className="text-xs text-zinc-500">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </header>
        <main className="flex-1 min-w-0 bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
